import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Toast from "@moltech/molui/toast";
import { usePostDataMutation } from "../../app/services/commonApi";
import {
  getAiApiMessage,
  isAiApiSuccess,
  formatAiErrorMessage,
} from "../../pages/aiPlatform/utils/aiApiResponse";
import { useCopilotShell } from "./CopilotShellContext";
import {
  buildChatContext,
  draftPromptFromSuggestion,
  hasUnresolvedEntityToken,
  isCompleteSuggestion,
  isImmediatePrompt,
  normalizeAssistantMessage,
  pick,
  resolveActiveListSession,
  resolveActiveWorkflow,
} from "./copilotUtils";

export const failedAssistantMessage = (errorText) => ({
  role: "assistant",
  content: "Something went wrong. Try again.",
  errorText: errorText || "MolAssist failed.",
  isError: true,
  toolsRan: [],
  suggestedActions: [],
  toolResults: [],
  blocks: [],
  draftEmailText: null,
  draftEmailSubject: null,
  draftEmailRecipients: [],
  workflow: null,
  listSession: null,
  pendingEntityQuery: null,
  lastListFilters: null,
  lastMetricCode: null,
  pendingConfirmation: null,
  aiExecutionLogId: null,
});

export const useCopilotChatSession = ({
  jobOpeningIdProp,
  isUploading,
  input,
  setInput,
}) => {
  const [searchParams] = useSearchParams();
  const { pageContext, threads: threadApi, audienceProfile } = useCopilotShell();
  const [postData, { isLoading }] = usePostDataMutation();
  const [isSendingEmailDraft, setIsSendingEmailDraft] = useState(false);

  const jobOpeningIdParam = jobOpeningIdProp ?? searchParams.get("jobOpeningId");
  const deepLinkQuery = searchParams.get("q");

  const {
    activeThread,
    ensureActiveThread,
    setThreadMessages,
    setThreadWorkflow,
    setThreadEntityEcho,
    setThreadConversationId,
    maybeSetThreadTitle,
    clearAllThreads,
  } = threadApi;

  const thread = activeThread || ensureActiveThread();
  const messages = thread?.messages || [];
  const workflow = thread?.workflow || null;

  const fillComposer = useCallback((text) => {
    setInput(draftPromptFromSuggestion(text));
    requestAnimationFrame(() => {
      document.querySelector(".copilot-composer__input")?.focus?.();
    });
  }, [setInput]);

  const send = useCallback(
    async (rawMessage, workflowOverride, options = {}) => {
      const message = String(rawMessage || "").trim();
      if (/^clear(\s+chat)?$/i.test(message)) {
        clearAllThreads();
        setInput("");
        Toast.success("Chat cleared.");
        return;
      }
      if (!message || isLoading || isUploading) return;
      if (hasUnresolvedEntityToken(message)) {
        setInput(message);
        Toast.info("Pick an item from the @ dropdown before sending.");
        requestAnimationFrame(() => {
          document.querySelector(".copilot-composer__input")?.focus?.();
        });
        return;
      }

      const current = ensureActiveThread();
      const quiet = Boolean(options.quiet);
      const userCount = current.messages.filter((m) => m.role === "user").length;
      if (!quiet && userCount === 0) maybeSetThreadTitle(current.id, message);

      const nextMessages = quiet
        ? current.messages
        : [...current.messages, { role: "user", content: message }];
      if (!quiet) {
        setThreadMessages(current.id, nextMessages);
        setInput("");
      }

      try {
        const history = nextMessages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .slice(-8)
          .map((m) => ({ role: m.role, content: m.content }));

        const workflowState =
          workflowOverride !== undefined
            ? workflowOverride
            : resolveActiveWorkflow(current);

        const context = buildChatContext({
          pageContext: current.pageContext || pageContext,
          workflow: workflowState,
          attachments: current.attachments,
          jobOpeningIdParam,
          listSession: resolveActiveListSession(current),
          pendingEntityQuery: current.pendingEntityQuery,
          lastListFilters: current.lastListFilters,
          lastMetricCode: current.lastMetricCode,
          audienceProfile,
        });

        const response = await postData({
          url: "/AIExecution/AssistantChat",
          data: {
            conversationId: current.conversationId,
            message,
            history,
            enableTools: true,
            context,
          },
        }).unwrap();

        if (!isAiApiSuccess(response)) {
          const errorText = formatAiErrorMessage(
            getAiApiMessage(response),
            "MolAssist request failed."
          );
          Toast.error(errorText);
          if (!quiet) {
            setThreadMessages(current.id, [
              ...nextMessages,
              failedAssistantMessage(errorText),
            ]);
          }
          return;
        }

        const data = pick(response, "data", "Data") || response;
        const assistantMsg = normalizeAssistantMessage(data);
        const nextConversationId =
          pick(data, "conversationId", "ConversationId") || current.conversationId;

        setThreadConversationId(current.id, nextConversationId);
        setThreadWorkflow(current.id, assistantMsg.workflow);
        setThreadEntityEcho(current.id, {
          listSession: assistantMsg.listSession,
          pendingEntityQuery: assistantMsg.pendingEntityQuery,
          lastListFilters: assistantMsg.lastListFilters,
          lastMetricCode: assistantMsg.lastMetricCode,
        });

        if (quiet) {
          const msgs = [...current.messages];
          for (let i = msgs.length - 1; i >= 0; i -= 1) {
            if (msgs[i].role === "assistant") {
              msgs[i] = { ...msgs[i], ...assistantMsg };
              break;
            }
          }
          setThreadMessages(current.id, msgs);
        } else {
          setThreadMessages(current.id, [...nextMessages, assistantMsg]);
        }
      } catch (err) {
        const errorText = formatAiErrorMessage(
          err?.data?.message || err?.data?.Message || err?.message,
          "MolAssist failed."
        );
        Toast.error(errorText);
        if (!quiet) {
          setThreadMessages(current.id, [
            ...nextMessages,
            failedAssistantMessage(errorText),
          ]);
        }
      }
    },
    [
      audienceProfile,
      clearAllThreads,
      ensureActiveThread,
      isLoading,
      isUploading,
      jobOpeningIdParam,
      maybeSetThreadTitle,
      pageContext,
      postData,
      setInput,
      setThreadConversationId,
      setThreadEntityEcho,
      setThreadMessages,
      setThreadWorkflow,
    ]
  );

  const runSuggestion = useCallback(
    (text) => {
      const drafted = draftPromptFromSuggestion(text);
      if (
        isImmediatePrompt(text) ||
        isImmediatePrompt(drafted) ||
        isCompleteSuggestion(text)
      ) {
        send(drafted || text);
      } else {
        fillComposer(text);
      }
    },
    [fillComposer, send]
  );

  const onSendEmailDraft = useCallback(
    async (draft) => {
      const subject = draft?.subject || "";
      const recipients = Array.isArray(draft?.recipients) ? draft.recipients : [];

      if (!subject || recipients.length === 0) return;

      setIsSendingEmailDraft(true);
      try {
        const result = await postData({
          url: "/AIExecution/SendCopilotShortlistEmail",
          data: {
            subject,
            recipients: recipients.map((r) => ({
              applicantId: r.applicantId ?? null,
              applicantName: r.applicantName ?? null,
              applicantEmail: r.applicantEmail ?? null,
              jobOpeningName: r.jobOpeningName ?? null,
              body: r.body ?? null,
            })),
          },
        }).unwrap();

        if (result) {
          Toast.success("Emails sent successfully.");
        } else {
          Toast.error("Failed to send emails.");
        }
      } catch (err) {
        Toast.error(err?.data?.message || err?.message || "Failed to send emails.");
        throw err;
      } finally {
        setIsSendingEmailDraft(false);
      }
    },
    [postData]
  );

  useEffect(() => {
    if (!deepLinkQuery) return;
    send(deepLinkQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount deep-link only
  }, []);

  return {
    thread,
    messages,
    workflow,
    isLoading,
    isSendingEmailDraft,
    jobOpeningIdParam,
    pageContext,
    threadApi,
    send,
    runSuggestion,
    fillComposer,
    onSendEmailDraft,
  };
};
